import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import FinancePageHeader from "../components/finance/FinancePageHeader";
import DashboardLayout from "../components/Layout/DashboardLayout";

const formatMoney = (value, currency) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString()} ${currency || ""}`.trim();
};

const CashboxCard = ({ row }) => {
  const current = Number(row.current_balance || 0);
  const minimum = Number(row.minimum_alert_balance || 0);

  const isCritical = minimum > 0 && current <= 0;
  const isWarning = minimum > 0 && current > 0 && current <= minimum;

  return (
    <Card
      sx={{
        borderRadius: 3,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
      }}
    >
      <CardContent>
        <Stack spacing={1.25}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="overline" color="text.secondary">
              Devise
            </Typography>

            <Chip
              size="small"
              label={row.is_active ? "Active" : "Inactive"}
              color={row.is_active ? "success" : "default"}
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            {row.currency || "-"}
          </Typography>

          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {formatMoney(row.current_balance, row.currency)}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Seuil minimum : {formatMoney(row.minimum_alert_balance, row.currency)}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Mise à jour :{" "}
            {row.updated_at ? new Date(row.updated_at).toLocaleString() : "-"}
          </Typography>

          {isCritical && <Alert severity="error">Caisse vide ou négative</Alert>}
          {isWarning && (
            <Alert severity="warning">Caisse sous le seuil minimum</Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const CashboxesPage = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [cashboxes, setCashboxes] = useState([]);
  const [movements, setMovements] = useState([]);
  const [fundings, setFundings] = useState([]);
  const [loading, setLoading] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";
    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [user]);

  const fetchData = useCallback(async () => {
    if (!user?.agency_id) return;

    try {
      setLoading(true);

      const [cashboxesRes, movementsRes, fundingsRes] = await Promise.all([
        api.get("/cashboxes", { params: { agency_id: user.agency_id } }),
        api.get("/cash-movements", { params: { agency_id: user.agency_id } }),
        api.get("/cash-fundings", { params: { agency_id: user.agency_id } }),
      ]);

      setCashboxes(Array.isArray(cashboxesRes.data) ? cashboxesRes.data : []);
      setMovements(
        Array.isArray(movementsRes.data) ? movementsRes.data.slice(0, 15) : []
      );
      setFundings(
        Array.isArray(fundingsRes.data) ? fundingsRes.data.slice(0, 10) : []
      );
    } catch (err) {
      enqueueSnackbar("Échec du chargement des données de caisse", {
        variant: "error",
      });
      setCashboxes([]);
      setMovements([]);
      setFundings([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout
      role={user?.role}
      activePage="/cashboxes"
      title="Gestion des caisses"
    >
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Stack spacing={2.5}>
          <FinancePageHeader
            title="Gestion des caisses"
            subtitle="Consultez les soldes par devise, les derniers mouvements et les approvisionnements récents de votre agence."
          />

          <Alert
            severity="info"
            sx={{ borderRadius: 3, alignItems: "center" }}
          >
            <strong>Agence :</strong>{" "}
            {assignedAgencyLabel || `Agence #${user?.agency_id || "non définie"}`}
          </Alert>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            <Button variant="contained" onClick={() => navigate("/cash-fundings")}>
              Nouvel approvisionnement
            </Button>
            <Button variant="outlined" onClick={() => navigate("/expenses")}>
              Dépenses
            </Button>
            <Button variant="outlined" onClick={() => navigate("/salary-payments")}>
              Salaires
            </Button>
            <Button variant="outlined" onClick={() => navigate("/loans")}>
              Prêts
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/financial-dashboard")}
            >
              Tableau de bord
            </Button>
            <Button variant="text" onClick={fetchData}>
              Actualiser
            </Button>
          </Stack>

          {loading ? (
            <Paper sx={{ p: 4, borderRadius: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            </Paper>
          ) : (
            <>
              {cashboxes.length === 0 ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Aucune caisse trouvée pour cette agence.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {cashboxes.map((row) => (
                    <Grid item xs={12} md={4} key={row.id}>
                      <CashboxCard row={row} />
                    </Grid>
                  ))}
                </Grid>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Derniers mouvements de caisse
                    </Typography>

                    {movements.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Aucun mouvement trouvé.
                      </Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Sens</TableCell>
                              <TableCell>Montant</TableCell>
                              <TableCell>Devise</TableCell>
                              <TableCell>Par</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {movements.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell>
                                  {row.created_at
                                    ? new Date(row.created_at).toLocaleString()
                                    : "-"}
                                </TableCell>
                                <TableCell>{row.movement_type || "-"}</TableCell>
                                <TableCell>{row.direction || "-"}</TableCell>
                                <TableCell>{row.amount ?? "-"}</TableCell>
                                <TableCell>{row.currency || "-"}</TableCell>
                                <TableCell>{row.created_by_name || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                      Derniers approvisionnements
                    </Typography>

                    {fundings.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Aucun approvisionnement trouvé.
                      </Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Montant</TableCell>
                              <TableCell>Devise</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell>Par</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fundings.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell>{row.funding_date || "-"}</TableCell>
                                <TableCell>{row.amount ?? "-"}</TableCell>
                                <TableCell>{row.currency || "-"}</TableCell>
                                <TableCell>{row.source || "-"}</TableCell>
                                <TableCell>{row.created_by_name || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default CashboxesPage;